/**
 * Shared cadence flow visualization.
 * Used in contact profile modal AND cadence detail side drawer.
 *
 * Features:
 * - Graph-aware step numbering via walkCadenceGraph (1, 2a, 2b)
 * - Real activity data: completed steps show green check + timestamps
 * - Current step highlighted, future steps greyed out
 * - Branches default collapsed, expandable, show which path contact took
 * - Wait pills between steps
 * - Compact mode for side drawer (hides TYPE/MODE/DATE columns)
 */
import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Icon, Loading } from '@evertrue/et-components';
import { walkCadenceGraph, getStepBadgeColor } from '../../utils/cadence-step-utils';
import { formatShortDate } from '../../utils/cadence-data-mappers';
import { isEarlyExit } from '../../utils/cadence-enums';
import StepLabelBlock from '../shared/step-label-block';

const CadenceFlowVisualization = ({
  cadence,
  loading,
  error,
  activities,
  currentEventId,
  memberStatus,
  displayStatus,
  compact,
}) => {
  const [expandedSteps, setExpandedSteps] = useState({});
  const [expandedBranches, setExpandedBranches] = useState({});
  const { items } = useMemo(
    () => walkCadenceGraph(cadence?.events || [], cadence?.edges || []),
    [cadence?.events, cadence?.edges]
  );

  const activityMap = useMemo(() => {
    if (!activities?.length) return {};
    const map = {};
    for (const a of activities) {
      const eid = String(a.cadenceEventId);
      if (!eid || eid === 'null') continue;
      if (!map[eid]) map[eid] = { types: new Set(), items: [] };
      map[eid].types.add(a.activityType);
      map[eid].items.push(a);
    }
    return map;
  }, [activities]);

  const visitedEventIds = useMemo(() => {
    const visited = new Set();
    for (const a of activities || []) {
      if (a.cadenceEventId) visited.add(String(a.cadenceEventId));
    }
    return visited;
  }, [activities]);

  const contactBranchPath = useMemo(() => {
    const paths = {};
    for (const item of items) {
      if (item.type !== 'branch') continue;
      const trueVisited = item.trueBranch.some((s) => visitedEventIds.has(String(s.id)));
      const falseVisited = item.falseBranch.some((s) => visitedEventIds.has(String(s.id)));
      if (trueVisited) paths[item.event?.id] = 'true';
      else if (falseVisited) paths[item.event?.id] = 'false';
    }
    return paths;
  }, [items, visitedEventIds]);

  const hasRemovalActivity = activities?.some((a) => a.activityType === 'removed' || a.activityType === 'completed');
  const isMemberDone =
    memberStatus === 'completed' ||
    memberStatus === 'removed' ||
    isEarlyExit(displayStatus) ||
    displayStatus === 'completed' ||
    hasRemovalActivity;

  if (loading) {
    return (
      <div className="cadence-profile-modal--flow-table cadence-profile-modal--flow-table-loading">
        <Loading />
      </div>
    );
  }

  if (!cadence && error) {
    return (
      <div className="cadence-profile-modal--flow-table cadence-profile-modal--flow-table-empty">
        <Icon icon="lock" style={{ fontSize: 20, marginBottom: 4 }} />
        <div>You don&#39;t have permission to view this cadence&#39;s details.</div>
      </div>
    );
  }

  if (!cadence?.events || cadence.events.length === 0) {
    return (
      <div className="cadence-profile-modal--flow-table cadence-profile-modal--flow-table-empty">
        No steps available
      </div>
    );
  }

  const toggleStep = (id) => setExpandedSteps((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleBranch = (id) => setExpandedBranches((prev) => ({ ...prev, [id]: !prev[id] }));

  const getStepState = (item) => {
    const eid = String(item.id);
    const info = activityMap[eid];
    const isCurrentStep = String(currentEventId) === eid;
    if (info) {
      const types = info.types;
      if (types.has('step_skipped') || types.has('task_skipped')) return 'skipped';
      if (
        types.has('task_completed') ||
        types.has('step_executed') ||
        types.has('email_delivered') ||
        types.has('email_sent')
      ) {
        return 'completed';
      }
      if (!isMemberDone && (types.has('task_created') || types.has('waiting'))) return 'current';
    }
    if (isCurrentStep && !isMemberDone) return 'current';
    if (isMemberDone) return 'not_reached';
    if (visitedEventIds.size > 0 && !visitedEventIds.has(eid)) return 'future';
    return 'pending';
  };

  const getStepDate = (item) => {
    const info = activityMap[String(item.id)];
    if (info?.items?.length) {
      const first = info.items[info.items.length - 1];
      return formatShortDate(first.activityAt) || item.timing || '\u2014';
    }
    return item.timing || '\u2014';
  };

  const renderSubEvents = (item) => {
    const info = activityMap[String(item.id)];
    if (!info?.items?.length) return null;
    const relevant = info.items.filter((a) =>
      [
        'email_sent',
        'email_delivered',
        'email_opened',
        'email_clicked',
        'email_replied',
        'unsubscribed',
        'task_created',
        'task_completed',
      ].includes(a.activityType)
    );
    if (!relevant.length) return null;
    const SUB_ICONS = {
      email_sent: 'send',
      email_delivered: 'check-circle',
      email_opened: 'eye',
      email_clicked: 'eye',
      email_replied: 'check',
      unsubscribed: 'close',
      task_created: 'access-time',
      task_completed: 'check-circle',
    };
    const SUB_LABELS = {
      email_sent: 'Sent',
      email_delivered: 'Delivered',
      email_opened: 'Opened',
      email_clicked: 'Clicked',
      email_replied: 'Replied',
      unsubscribed: 'Unsubscribed',
      task_created: 'Task Created',
      task_completed: 'Task Completed',
    };
    return (
      <div className="cadence-profile-modal--flow-subevents">
        {relevant.map((a, i) => (
          <div key={`${a.activityType}-${i}`} className="cadence-profile-modal--flow-subevent-row">
            <Icon icon={SUB_ICONS[a.activityType] || 'info'} className="cadence-profile-modal--flow-subevent-icon" />
            <span className="cadence-profile-modal--flow-subevent-label">
              {SUB_LABELS[a.activityType] || a.activityType}
            </span>
            <span className="cadence-profile-modal--flow-subevent-date">
              {formatShortDate(a.activityAt) || '\u2014'}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderStepRow = (item, key, opts = {}) => {
    const state = getStepState(item);
    const isOpen = expandedSteps[item.id];
    const isFuture = state === 'future' || state === 'not_reached';
    const stepDate = getStepDate(item);

    const stateClass =
      state === 'completed'
        ? 'is-completed'
        : state === 'current'
          ? 'is-current'
          : state === 'skipped'
            ? 'is-skipped'
            : isFuture
              ? 'is-future'
              : '';

    return (
      <div
        key={key}
        className={`cadence-profile-modal--flow-row ${stateClass}`}
        style={isFuture ? { opacity: 0.45 } : undefined}
      >
        <div className="cadence-profile-modal--flow-row-main" role="row">
          <div className="cadence-profile-modal--flow-row-status">
            {state === 'completed' ? (
              <span className="cadence-profile-modal--flow-status-icon is-completed">
                <Icon icon="check" style={{ fontSize: 14, color: '#fff' }} />
              </span>
            ) : state === 'skipped' ? (
              <span className="cadence-profile-modal--flow-status-icon is-skipped">
                <Icon icon="remove" style={{ fontSize: 14, color: '#fff' }} />
              </span>
            ) : state === 'current' ? (
              <span className="cadence-profile-modal--flow-status-icon is-current">
                <Icon icon={item.actionIcon || 'task'} style={{ fontSize: 14 }} />
              </span>
            ) : (
              <span className="cadence-profile-modal--flow-status-icon is-pending">
                <Icon icon={item.actionIcon || 'task'} style={{ fontSize: 14 }} />
              </span>
            )}
          </div>
          <div className="cadence-profile-modal--flow-row-action">
            {opts.condBadge && (
              <span className={`cadence-profile-modal--flow-cond-badge is-${opts.condBadgeType || 'true'}`}>
                {opts.condBadge}
              </span>
            )}
            <StepLabelBlock
              title={item.name && item.name !== item.actionType ? item.name : undefined}
              typeLabel={item.actionType}
              typeIcon={item.actionIcon}
              badgeColor={getStepBadgeColor({
                type: (item.actionType || '').toLowerCase(),
                isAutomated: item.mode === 'Automated',
              })}
              modeSubline={
                item.event?.cadenceEventReferenceNumber != null
                  ? `${item.mode} (ID ${item.event.cadenceEventReferenceNumber})`
                  : undefined
              }
            />
            {state === 'not_reached' && (
              <span className="cadence-profile-modal--flow-row-action-sub">Will not be sent</span>
            )}
          </div>
          {!compact && (
            <div className="cadence-profile-modal--flow-row-date">
              {state === 'completed' || state === 'current' ? stepDate : '\u2014'}
            </div>
          )}
          {compact && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 500,
                color:
                  state === 'completed'
                    ? '#16a34a'
                    : state === 'current'
                      ? '#f97316'
                      : state === 'skipped'
                        ? '#9ca3af'
                        : 'transparent',
                marginLeft: 'auto',
              }}
            >
              {state === 'completed'
                ? activityMap[String(item.id)]?.types?.has('email_delivered')
                  ? 'Delivered'
                  : activityMap[String(item.id)]?.types?.has('email_sent')
                    ? 'Sent'
                    : activityMap[String(item.id)]?.types?.has('task_completed')
                      ? 'Task Completed'
                      : 'Completed'
                : state === 'current'
                  ? 'In Progress'
                  : state === 'skipped'
                    ? 'Skipped'
                    : ''}
            </span>
          )}
          <div className="cadence-profile-modal--flow-row-expand">
            {(state === 'completed' || (compact && activityMap[String(item.id)]?.items?.length > 0)) && (
              <button
                type="button"
                className="cadence-profile-modal--flow-expand-btn"
                onClick={() => toggleStep(item.id)}
              >
                <Icon icon={isOpen ? 'chevron-up' : 'chevron-down'} />
              </button>
            )}
          </div>
        </div>
        {isOpen && renderSubEvents(item)}
      </div>
    );
  };

  const renderWait = (item, key) => {
    const cfg = item.event?.eventConfig || {};
    const days = cfg.duration_days || 0;
    const hours = cfg.duration_hours || 0;
    const minutes = cfg.duration_minutes || 0;
    const parts = [];
    if (days) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    if (hours) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    if (minutes) parts.push(`${minutes} min`);
    const label = parts.length ? parts.join(' ') : `${item.durationDays || 0} days`;

    return (
      <div key={key} className="cadence-profile-modal--flow-wait">
        <div className="cadence-profile-modal--flow-wait-line" />
        <div className="cadence-profile-modal--flow-wait-pill">
          <Icon icon="access-time" style={{ fontSize: 14 }} />
          <span>Wait {label}</span>
        </div>
      </div>
    );
  };

  const renderBranch = (item, idx) => {
    const branchId = item.event?.id || idx;
    const isOpen = expandedBranches[branchId] || false;
    const contactPath = contactBranchPath[branchId];

    return (
      <div key={`branch-${idx}`} className="cadence-profile-modal--flow-branch-group">
        <button
          type="button"
          className="cadence-profile-modal--flow-branch-label"
          onClick={() => toggleBranch(branchId)}
        >
          <Icon icon="call-split" className="cadence-profile-modal--flow-branch-split-icon" />
          <span>Branch Point: {item.conditionName}</span>
          <Icon
            icon={isOpen ? 'expand-less' : 'expand-more'}
            className="cadence-profile-modal--flow-branch-expand-icon"
          />
        </button>

        {isOpen && (
          <>
            {item.trueBranch.length > 0 && (
              <div className="cadence-profile-modal--flow-branch-path is-true">
                <div className="cadence-profile-modal--flow-branch-path-header">
                  <span>&#8592; YES &#8211; {item.condLabels?.yesHeader || 'True path'}</span>
                  {contactPath === 'true' && (
                    <span className="cadence-profile-modal--flow-branch-contact-badge">&#9679; Contact&#39;s path</span>
                  )}
                </div>
                {item.trueBranch.map((s, i) =>
                  s.type === 'wait'
                    ? renderWait(s, `branch-true-wait-${i}`)
                    : renderStepRow(s, `step-${s.id}`, { condBadge: s.condBadge, condBadgeType: 'true' })
                )}
              </div>
            )}
            {item.falseBranch.length > 0 && (
              <div className="cadence-profile-modal--flow-branch-path is-false">
                <div className="cadence-profile-modal--flow-branch-path-header">
                  <span>&#8592; NO &#8211; {item.condLabels?.noHeader || 'False path'}</span>
                  {contactPath === 'false' && (
                    <span className="cadence-profile-modal--flow-branch-contact-badge">&#9679; Contact&#39;s path</span>
                  )}
                </div>
                {item.falseBranch.map((s, i) =>
                  s.type === 'wait'
                    ? renderWait(s, `branch-false-wait-${i}`)
                    : renderStepRow(s, `step-${s.id}`, { condBadge: s.condBadge, condBadgeType: 'false' })
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="cadence-profile-modal--flow-table">
      {!compact && (
        <div className="cadence-profile-modal--flow-header" role="row">
          <div className="cadence-profile-modal--flow-header-cell is-status" />
          <div className="cadence-profile-modal--flow-header-cell is-action">STEP</div>
          <div className="cadence-profile-modal--flow-header-cell is-date">DATE</div>
          <div className="cadence-profile-modal--flow-header-cell is-expand" />
        </div>
      )}

      {items.map((item, idx) => {
        if (item.type === 'wait') return renderWait(item, `wait-${idx}`);
        if (item.type === 'step') return renderStepRow(item, `step-${item.id}`);
        if (item.type === 'branch') return renderBranch(item, idx);
        return null;
      })}
    </div>
  );
};

CadenceFlowVisualization.propTypes = {
  cadence: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  activities: PropTypes.array,
  currentEventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  memberStatus: PropTypes.string,
  displayStatus: PropTypes.string,
  compact: PropTypes.bool,
};

export default CadenceFlowVisualization;
